import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEmpty, reduce, cloneDeep, get, sortBy, forEach, uniqBy, isEqual } from 'lodash';
import asyncRoute from '../../../helpers/hoc/asyncRoute';
import {
  customErrorCodes,
  exceptionComponentPageTypes,
  toastMessageTime,
  apiResponseCodes,
} from '../../../constants/globals';
import {
  getButtonHandlerCta,
  getErrorDescription,
  getErrorTitle,
  selectRestaurant,
} from '../../../selectors';
import { constants } from '../../../constants/configuration';
import routes from '../../../routes';
import {
  clearPreviousCustomization,
  fetchMenuItemCustomizationsAction,
  fetchViewCartItemsAction,
} from '../../../actions/app.action';
import './AvailOffer.scss';

const Header = asyncRoute(() => import('../../../components/Header'));
const FoodItem = asyncRoute(() => import('../../../components/FoodItem'));
const Description = asyncRoute(() => import('../../../components/elements/Description'));
const Button = asyncRoute(() => import('../../../components/elements/Button'));
const CustomizationMenuPopup = asyncRoute(() =>
  import('../../../components/Popup/CustomizationMenu'),
);
const MoreItemsPopup = asyncRoute(() => import('../../../components/Popup/MoreItems'));
const Toast = asyncRoute(() => import('../../../components/elements/Toast'));
const Footer = asyncRoute(() => import('../../../components/Footer'));
const ExceptionComponent = asyncRoute(() => import('../../../components/ExceptionComponent'));

const offersConfig = constants.offers;

let maxItemSelectionCount = 0;

class AvailOffer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isCustomizable: false,
      customizeItem: {},
      categories: [],
      items: [],
      countMap: {},
      selectedCustomization: [],
      openRepeatCustomizationPopup: false,
      currentItem: {},
      addNewItemFlag: false,
      showAuthenticationPopup: false,
      showMultipleCustomizationPopup: false,
      showMoreItemsPopup: false,
      availableOffer: {},
      appliedOffers: [],
      isShowOffers: false,
      menuCategoryCountMap: {},
      toast: '',
      remainingMenuCategoryItems: [],
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      errorPopupCancellable: true,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      showCustomizationErrorCTA: false,
    };
    this.scrollRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { toast } = this.state;
    // Generic toast state
    if (!isEmpty(toast) && !isEqual(toast, prevProps.toast)) {
      setTimeout(
        () =>
          this.setState({
            toast: '',
          }),
        toastMessageTime,
      );
    }
  }

  componentDidMount() {
    const { match, viewCartItems, availableOffers } = this.props;

    // If viewCartItems or availableOffers app state in store is empty due to contextless hit to offer-list url, show error
    if (isEmpty(viewCartItems) || isEmpty(availableOffers)) {
      this.setState({
        showErrorPopup: true,
        showContactInfoPopup: false,
        errorContent: {
          title: getErrorTitle({ code: customErrorCodes.contextlessURLHit }),
          description: getErrorDescription({ code: customErrorCodes.contextlessURLHit }),
          buttonCta: getButtonHandlerCta({ code: customErrorCodes.contextlessURLHit }),
        },
        handleErrorCTA: this.handleContextlessURLHitErrorPopupCTA,
        errorPopupStyleType: exceptionComponentPageTypes.fullSize,
      });
    } else {
      // Else, populate the component state with offer data
      const offerId = get(match, 'params.offerId', '');
      const availableOffer = availableOffers.find((offer) => offer.id === offerId);
      const offerSelectableMenu = get(availableOffer, 'offerSelectableMenu.sections', []);
      maxItemSelectionCount = get(availableOffer, 'offerSelectableMenu.maxSelectionCount', 0);
      let countMap = {};
      let menuCategoryCountMap = {};
      reduce(
        offerSelectableMenu,
        (result, { id, name, categories, menuItems, maxSelectionCount }) => {
          result.push({ id, name });
          // if categories is not empty then populate it
          if (isEmpty(categories) && !isEmpty(menuItems)) {
            for (const item of menuItems) {
              if (item.isEnabled) {
                // make zero for default
                countMap[item.id] = { quantity: 0 };
              }
            }
          } else {
            reduce(
              categories,
              (acc, { menuItems }) => {
                if (!isEmpty(menuItems)) {
                  for (const item of menuItems) {
                    if (item.isEnabled) {
                      // make zero for default
                      countMap[item.id] = { quantity: 0 };
                    }
                  }
                }
                return acc;
              },
              [],
            );
          }
          if (maxSelectionCount && maxSelectionCount > 0) {
            menuCategoryCountMap[id] = { maxSelectionCount };
          }
          return result;
        },
        [],
      );
      this.setState({
        availableOffer,
        countMap,
        menuCategoryCountMap,
        maxItemSelectionCount,
      });
    }

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  onCloseExceptionComponent = () => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
    });
  };

  onBackClick = () => {
    this.props.history.push(`/${routes.containers.offers}`);
  };

  onAddItem = ({ id, price, menuCategoryDto }, selectedCustomization) => {
    const { countMap, items, addNewItemFlag, customizeItem, menuCategoryCountMap } = this.state;
    let countMapState = cloneDeep(countMap, true);
    let currentCompareKey = id;
    let index;
    if (addNewItemFlag || (selectedCustomization && !isEmpty(selectedCustomization))) {
      currentCompareKey = this.generateCompareKey(id, selectedCustomization);
    } else {
      currentCompareKey = countMapState[id].last;
    }
    if (
      currentCompareKey &&
      customizeItem &&
      !isEmpty(customizeItem) &&
      customizeItem.isCustomizable
    ) {
      index = items.findIndex(
        (x) => this.generateCompareKey(x.menuItemId, x.orderCustomizations) === currentCompareKey,
      );
    } else {
      index = items.findIndex((x) => x.menuItemId === id);
    }
    if (index > -1) {
      items[index].quantity += 1;
      items[index].addNewItemFlag = addNewItemFlag;
    } else {
      items.push({
        menuItemId: id,
        customization: get(customizeItem, 'description', ''),
        quantity: 1,
        orderCustomizations: selectedCustomization,
        price,
        name: get(menuCategoryDto, 'name', ''),
        menuCategoryId: get(menuCategoryDto, 'id', ''),
      });
    }
    if (
      !currentCompareKey &&
      customizeItem &&
      !isEmpty(customizeItem) &&
      customizeItem.isCustomizable
    ) {
      currentCompareKey = this.generateCompareKey(id, selectedCustomization);
    }
    countMapState[id].quantity += 1;
    countMapState[id].last = currentCompareKey;
    if (!isEmpty(menuCategoryCountMap)) {
      menuCategoryCountMap[menuCategoryDto.id].maxSelectionCount -= 1;
    } else {
      maxItemSelectionCount -= 1;
    }
    this.setState({
      countMap: countMapState,
      items,
      addNewItemFlag: false,
      customizeItem: {},
      selectedCustomization,
      menuCategoryCountMap,
    });
  };

  generateCompareKey = (id, selectedCustomization) => {
    let itemIdsArr = [];
    itemIdsArr.push(id);
    if (selectedCustomization) {
      for (const customization of selectedCustomization) {
        itemIdsArr.push(customization.menuCustomizationId);
        sortBy(itemIdsArr);
      }
    }
    return itemIdsArr.join('~');
  };

  onFoodAddClick = (item) => {
    const { countMap, menuCategoryCountMap } = this.state;
    const { isCustomizable } = item;
    const { restaurant, customization } = this.props;
    const restaurantId = get(restaurant, 'id', '');
    const menuCategoryAvailableCount = !isEmpty(menuCategoryCountMap)
      ? menuCategoryCountMap[get(item, 'menuCategoryDto.id', '')].maxSelectionCount
      : maxItemSelectionCount;
    if (menuCategoryAvailableCount > 0) {
      if (isCustomizable) {
        if (countMap[item.id] && countMap[item.id].last) {
          this.setState({
            isCustomizable: true,
            openRepeatCustomizationPopup: !isEmpty(customization) ? true : false,
            customizeItem: { ...item },
            currentItem: item,
          });
        } else {
          this.setState(
            {
              isCustomizable: true,
              customizeItem: { ...item },
              openRepeatCustomizationPopup: false,
              currentItem: item,
            },
            () => {
              this.props.clearPreviousCustomization().then(() => {
                this.props
                  .fetchMenuItemCustomizationsAction(restaurantId, item.id)
                  .then((response) => {
                    if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      this.setState({
                        showCustomizationErrorCTA: true,
                      });
                    }
                  });
              });
            },
          );
        }
      } else {
        this.onAddItem(item);
      }
    } else {
      this.setState({
        toast: !isEmpty(menuCategoryCountMap)
          ? offersConfig.toastMessage.categoryMaxSelectionReached
          : offersConfig.toastMessage.maxSelectionReached,
      });
    }
  };

  onFoodRemoveClick = (item) => {
    const { id, menuCategoryDto } = item;
    const { countMap, items, menuCategoryCountMap } = this.state;
    let countMapState = cloneDeep(countMap, true);
    const menuItemIndex = items.findIndex((item) => item.menuItemId === id);
    if (menuItemIndex > -1) {
      let menuItem = items.find((item) => item.menuItemId === id);
      menuItem.quantity = menuItem.quantity - 1;
      if (menuItem.quantity === 0) {
        items.splice(menuItemIndex, 1);
        countMapState[id].last = undefined;
      } else {
        items[menuItemIndex] = menuItem;
      }
      countMapState[id].quantity -= 1;
      if (!isEmpty(menuCategoryCountMap)) {
        menuCategoryCountMap[menuCategoryDto.id].maxSelectionCount += 1;
      } else {
        maxItemSelectionCount += 1;
      }
      this.setState({
        countMap: countMapState,
        items,
        menuCategoryCountMap,
      });
    }
  };

  onAddNewItem = () => {
    this.setState({
      openRepeatCustomizationPopup: false,
      addNewItemFlag: true,
    });
  };

  onRepeatLastItem = () => {
    const { currentItem, selectedCustomization } = this.state;
    this.setState(
      {
        openRepeatCustomizationPopup: false,
        isCustomizable: false,
      },
      () => {
        this.onAddItem(currentItem, selectedCustomization);
      },
    );
  };

  renderFoodItem = (menuItem) => {
    const { countMap } = this.state;
    const { id, name, price, isCustomizable, description, item, type, isEnabled } = menuItem;
    return (
      <FoodItem
        key={id}
        name={name}
        price={price}
        isEnabled={isEnabled}
        isCustomizable={isCustomizable}
        description={description}
        menuItem={item}
        type={type}
        count={countMap[id] && countMap[id].quantity}
        menuItem={menuItem}
        onFoodAddClick={this.onFoodAddClick}
        onFoodRemoveClick={this.onFoodRemoveClick}
        pageSrc={routes.offers.availOffer}
      />
    );
  };

  renderCategory = (categoryItems) => {
    let pushItem = [];
    for (const item of categoryItems.menuItems) {
      pushItem.push(
        this.renderFoodItem({
          ...item,
          item: { ...item },
        }),
      );
    }
    return (
      pushItem &&
      pushItem.length > 0 && (
        <div key={categoryItems.id}>
          <div className="avail-offer_category-item-name">
            {categoryItems.name}
            {categoryItems.description && (
              <Description className="avail-offer_categoryDescription">
                {categoryItems.description}
              </Description>
            )}
          </div>
          {pushItem}
        </div>
      )
    );
  };

  renderItems = ({ menu }) => {
    let index = 0;
    return reduce(
      menu,
      (result, { id, name, categories, menuItems, description, maxSelectionCount }) => {
        // check if there is a category
        const section = [];
        if (isEmpty(categories) && !isEmpty(menuItems)) {
          for (const item of menuItems) {
            section.push(
              this.renderFoodItem({
                ...item,
                item: { ...item },
              }),
            );
          }
          result.push(
            <div>
              {section && section.length > 0 && (
                <>
                  <div className="avail-offer_categoryName">
                    <div>
                      <span>{name}</span>
                      {maxSelectionCount > 0 && (
                        <span className="avail-offer_select-any-text">{`${offersConfig.selectAnyText} ${maxSelectionCount}`}</span>
                      )}
                    </div>
                    {description && (
                      <Description className="avail-offer_categoryDescription">
                        {description}
                      </Description>
                    )}
                  </div>
                  {section}
                  <div className="avail-offer_line-break" />
                  <div className="avail-offer_line-break" />
                </>
              )}
            </div>,
          );
          index++;
        } else {
          let categoryItemPush = [];
          if (!isEmpty(categories)) {
            for (const categoryItem of categories) {
              categoryItem.menuItems && categoryItemPush.push(this.renderCategory(categoryItem));
            }
          }
          result.push(
            <div>
              {categoryItemPush && categoryItemPush.length > 0 && (
                <>
                  <div className="avail-offer_categoryName">
                    {name}
                    {description && (
                      <Description className="avail-offer_categoryDescription">
                        {description}
                      </Description>
                    )}
                  </div>
                  {categoryItemPush}
                  <div className="avail-offer_line-break" />
                  <div className="avail-offer_line-break" />
                </>
              )}
            </div>,
          );
          index++;
        }
        return result;
      },
      [],
    );
  };

  onCustomizableHide = () => {
    this.setState({
      isCustomizable: false,
      showMenuItemDetailPopup: false,
    });
  };

  handleContinueAnywayClick = () => {
    const { viewCartItems, loginData } = this.props;
    let { availableOffer, appliedOffers, isShowOffers, items } = this.state;
    const { id: offerId } = availableOffer;
    appliedOffers.push(offerId);
    const selectedOffers = [
      {
        offerId,
        offeredItems: items,
      },
    ];
    const guestId = get(loginData, 'id', '');
    this.props
      .fetchViewCartItemsAction({
        ...viewCartItems,
        isShowOffers,
        appliedOffers,
        selectedOffers,
        guestId,
      })
      .then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
          this.props.history.push(`/${routes.containers.cart}`);
        }
      });
  };

  onCloseMoreItemsPopup = () => {
    this.setState({
      showMoreItemsPopup: false,
    });
  };

  handleApplyOfferClick = () => {
    const { viewCartItems, loginData } = this.props;
    let {
      availableOffer,
      appliedOffers,
      isShowOffers,
      menuCategoryCountMap,
      items,
      remainingMenuCategoryItems,
    } = this.state;

    let menuItemAvailableCount = 0;
    if (maxItemSelectionCount > 0) {
      menuItemAvailableCount = maxItemSelectionCount;
    } else {
      forEach(menuCategoryCountMap, function (value) {
        menuItemAvailableCount = menuItemAvailableCount + value.maxSelectionCount;
      });
    }
    if (menuItemAvailableCount > 0) {
      const menu = get(availableOffer, 'offerSelectableMenu.sections', []);
      let remainingCategoryItems = remainingMenuCategoryItems;
      if (maxItemSelectionCount > 0) {
        remainingCategoryItems = items && items.length > 0 && uniqBy(items, 'menuCategoryId');
      } else {
        remainingCategoryItems = menu.filter(
          (menuItem) => menuCategoryCountMap[menuItem.id].maxSelectionCount > 0,
        );
      }
      this.setState({
        showMoreItemsPopup: true,
        remainingMenuCategoryItems: remainingCategoryItems,
      });
    } else {
      const { id: offerId } = availableOffer;
      const guestId = get(loginData, 'id', '');
      appliedOffers.push(offerId);
      const selectedOffers = [
        {
          offerId,
          offeredItems: items,
        },
      ];
      this.props
        .fetchViewCartItemsAction({
          ...viewCartItems,
          isShowOffers,
          appliedOffers,
          selectedOffers,
          guestId,
        })
        .then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
            this.props.history.push(`/${routes.containers.cart}`);
          }
        });
    }
  };

  handleCustomizationErrorCTA = () => {
    const { restaurant } = this.props;
    const { currentItem } = this.state;
    let restaurantId = get(restaurant, 'id', '');
    this.setState(
      {
        showCustomizationErrorCTA: false,
      },
      () => {
        this.props.actions
          .fetchMenuItemCustomizationsAction(restaurantId, currentItem.id)
          .then((response) => {
            if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
              this.setState({
                showCustomizationErrorCTA: true,
              });
            }
          });
      },
    );
  };

  render() {
    const {
      availableOffer,
      customizeItem,
      selectedCustomization,
      openRepeatCustomizationPopup,
      isCustomizable,
      showMoreItemsPopup,
      toast,
      remainingMenuCategoryItems,
      menuCategoryCountMap,
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      errorPopupCancellable,
      errorPopupStyleType,
      maxItemSelectionCount,
      showCustomizationErrorCTA,
    } = this.state;
    const { offerSelectableMenu, code: offerCode } = availableOffer;
    const menu = get(offerSelectableMenu, 'sections', []);
    return (
      <>
        <Header
          onBackClick={this.onBackClick}
          identifier={routes.containers.offers}
          title={offerCode}
        />
        <div className="avail-offer">
          {!isEmpty(toast) && <Toast content={toast} />}
          {maxItemSelectionCount > 0 && (
            <div className="avail-offer_select-any-text">{`${offersConfig.selectAnyText} ${maxItemSelectionCount}`}</div>
          )}
          {this.renderItems({ menu })}
          <div className="avail-offer_btnCTA">
            <Button content={offersConfig.applyOfferCTA} onClick={this.handleApplyOfferClick} />
          </div>
          <div className="avail-offer_footer-branding">
            <Footer />
          </div>
        </div>
        {isCustomizable && (
          <CustomizationMenuPopup
            openSegmentPopup={isCustomizable}
            dismissSegmentPopup={this.onCloseCustomizationMenuPopup}
            name={get(customizeItem, 'name', '')}
            description={get(customizeItem, 'description', '')}
            price={get(customizeItem, 'price', 0)}
            onAddToCart={this.onAddToCart}
            onAddNewItem={this.onAddNewItem}
            onRepeatLastItem={this.onRepeatLastItem}
            openRepeatCustomizationPopup={openRepeatCustomizationPopup}
            selectedCustomization={selectedCustomization}
            showCustomizationErrorCTA={showCustomizationErrorCTA}
            handleCustomizationErrorCTA={this.handleCustomizationErrorCTA}
          />
        )}
        {showMoreItemsPopup && (
          <MoreItemsPopup
            openSegmentPopup={showMoreItemsPopup}
            dismissSegmentPopup={this.onCloseMoreItemsPopup}
            items={remainingMenuCategoryItems}
            menuCategoryCountMap={menuCategoryCountMap}
            maxItemSelectionCount={maxItemSelectionCount}
            handleContinueAnywayClick={this.handleContinueAnywayClick}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={errorPopupCancellable ? this.onCloseExceptionComponent : undefined} // Sending this prop as undefined as the SegmentComponent being used internally in ExceptionComponent implicitly handles it
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={handleErrorCTA}
          />
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    restaurant: selectRestaurant(state),
    viewCartItems: get(state, 'app.viewCart', {}),
    availableOffers: get(state, 'app.availableOffers', []),
    customization: get(state, 'app.customization'),
    loginData: get(state, 'authentication.loginData', {}),
  }),
  {
    fetchMenuItemCustomizationsAction,
    clearPreviousCustomization,
    fetchViewCartItemsAction,
  },
)(AvailOffer);
